import { Badge, Button, Code, Group, Modal, ScrollArea, Stack, Table, Text } from "@mantine/core";
import { FC, ReactNode, useEffect, useState } from "react";

import { copyToClipboard } from "helpers/clipboard";
import {
  describeDenialEvidenceAge,
  formatDiagnosticTimestamp,
  getDiagnostics,
  GeoDiagnostics,
  getInferredDenialAt,
  isLocationDenied,
  isStandalone,
} from "helpers/locationService";

/**
 * Field-support panel for diagnosing "the Locate button does nothing" reports.
 *
 * Deliberately ALWAYS compiled in (no env flag, no URL param): an env flag would
 * require a rebuild+redeploy to look at a rep's phone, and a URL param is
 * unusable in a standalone iOS PWA, which has no address bar. It is reached by a
 * 700ms long-press on the Locate icon.
 */

interface EnvironmentInfo {
  permissionState: string;
  /**
   * from `isStandalone()` in locationService — deliberately NOT re-derived here.
   * The same predicate picks the DENIED toast copy, so the recovery steps this
   * panel shows can never contradict the toast that sent the user to it.
   */
  standalone: boolean;
  navigatorStandalone: boolean | null;
  isSecureContext: boolean;
  hasGeolocation: boolean;
  origin: string;
  userAgent: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
}

const readEnvironment = async (): Promise<EnvironmentInfo> => {
  let permissionState = "unknown";
  try {
    // Safari's support for the Permissions API is inconsistent — it may be
    // missing entirely, or reject for the 'geolocation' name.
    const permissions = (navigator as any)?.permissions;
    if (permissions?.query) {
      const status = await permissions.query({ name: "geolocation" as PermissionName });
      permissionState = status?.state ?? "unknown";
    } else {
      permissionState = "unsupported";
    }
  } catch (e: any) {
    permissionState = `error: ${e?.message ?? String(e)}`;
  }

  // the raw navigator flag is still reported on its own row: with `standalone`
  // above it, support can tell WHICH of the two signals fired.
  const navigatorStandalone =
    typeof (navigator as any)?.standalone === "boolean"
      ? ((navigator as any).standalone as boolean)
      : null;

  return {
    permissionState,
    standalone: isStandalone(),
    navigatorStandalone,
    isSecureContext: !!window.isSecureContext,
    hasGeolocation: "geolocation" in navigator,
    origin: window.location?.origin ?? "",
    userAgent: navigator?.userAgent ?? "",
  };
};

const formatAge = (ms: number | null): string => {
  if (ms === null) return "n/a";
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)} s`;
  return `${Math.round(ms / 60000)} min`;
};

const LocationDebugPanel: FC<Props> = ({ opened, onClose }) => {
  const [env, setEnv] = useState<EnvironmentInfo | null>(null);
  const [diagnostics, setDiagnostics] = useState<GeoDiagnostics | null>(null);
  const [copyResult, setCopyResult] = useState<string>("");

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;

    setCopyResult("");
    setDiagnostics(getDiagnostics());
    // Reset FIRST. `env` survives a close (the effect only runs on open), so
    // without this the `denied` guard below would be evaluated against the
    // PREVIOUS open's permission result while this open's `permissions.query` is
    // still in flight — showing a user who has since fixed Settings the stale
    // recovery steps, briefly or, if the query never settles, indefinitely.
    // Every row already renders "…" for a null `env`, so the cost of resetting
    // is one frame of placeholders and the guard means what it claims.
    setEnv(null);
    readEnvironment().then((e) => {
      if (!cancelled) setEnv(e);
    });

    return () => {
      cancelled = true;
    };
  }, [opened]);

  const payload = {
    capturedAt: new Date().toISOString(),
    environment: env,
    diagnostics,
  };

  const onCopy = async () => {
    const ok = await copyToClipboard(JSON.stringify(payload, null, 2));
    setCopyResult(ok ? "Copied" : "Copy failed");
  };

  const row = (label: string, value: ReactNode) => (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text fz={13} c="#8B8B8B" style={{ whiteSpace: "nowrap" }}>{label}</Text>
      <Text fz={13} ta="right" style={{ wordBreak: "break-all" }}>{value}</Text>
    </Group>
  );

  /**
   * Whether to show the recovery path.
   *
   * Deliberately NOT `env?.permissionState === "denied"`. `readEnvironment`
   * above anticipates `navigator.permissions` being missing ("unsupported") or
   * rejecting ("error: ..."), which is the normal case on iOS — the exact
   * platform whose DENIED toast tells the user to long-press Locate for these
   * steps. That string compare hid this block precisely when it was needed.
   * `isLocationDenied` keeps the Permissions API authoritative when it answers
   * and otherwise falls back to the recorded geolocation attempts; it lives in
   * locationService because this component cannot be rendered under Jest (see
   * that function's comment), so the rule is covered by tests there.
   *
   * Only evaluated once `env` has loaded, so the block cannot flash open on the
   * ring buffer alone while the permission query is still in flight. That guard
   * is only honest because the effect above clears `env` at the start of every
   * open: `env` outlives a close, so without the reset "loaded" would silently
   * mean "loaded during some earlier open".
   */
  const denied = env ? isLocationDenied(env.permissionState) : false;
  const deniedIsInferred = !!env && env.permissionState !== "denied";
  /**
   * When the attempt behind an inferred verdict happened. Disclosed because the
   * inference is bounded to the page session but not to a freshness window: on
   * iOS a standalone PWA's page can stay alive for days, so the user may have
   * fixed Settings after that attempt without making a new one. Naming the time
   * lets the reader see how old the evidence is instead of trusting a bare
   * "blocked". See `getInferredDenialAt` for why a recency cap was rejected.
   *
   * AGE, NOT JUST A CLOCK TIME. Round 3 rendered this with
   * `toLocaleTimeString()`, which discloses a time of day and nothing else — so
   * in the exact multi-day case this disclosure exists for, a Monday denial read
   * on Tuesday looked like it happened today. Both halves are printed now: the
   * relative age answers the rep's only question ("is this from before or after
   * I changed the toggle?") without arithmetic, and the absolute stamp
   * (`toLocaleString()`, date AND time) lets support line it up against the
   * ring-buffer table below. The wording itself is `describeDenialEvidenceAge`
   * in the service, where the gate can cover it — this component cannot be
   * rendered under Jest.
   */
  const inferredDenialAt = env ? getInferredDenialAt(env.permissionState) : null;

  /**
   * The full recovery path, shown only when the OS has actually denied the
   * origin. It lives here rather than in the toast because the toast is a banner
   * over the map on a phone: it carries only the two facts that change what the
   * user does next (which Settings screen, and that reinstalling is wasted
   * effort) and points at this panel for the rest.
   *
   * NOT VERIFIED ON DEVICE: whether an installed PWA is listed in Location
   * Services under its own name or folded under "Safari Websites". Until the
   * tester confirms, step 2 deliberately names both candidates and says to check
   * both, rather than sending him to a row that may not exist.
   */
  const deniedRecovery = (
    <Stack gap={4} p="sm" style={{ border: "1px solid #E64B37", borderRadius: 8 }}>
      <Text fz={13} fw="bold" c="#E64B37">Location is blocked for this origin</Text>
      <Text fz={12}>
        iOS has remembered the denial for {env?.origin || "this site"}. The permission
        prompt will not come back on its own, so tapping Locate will keep failing
        until the Settings toggle below is changed.
      </Text>
      <Text fz={12} mt={4}>1. Open Settings → Privacy &amp; Security → Location Services.</Text>
      <Text fz={12}>
        2. Check that Location Services is on, then find the entry for this app.
        Depending on the iOS version it is listed either under the app&apos;s own name
        or under &quot;Safari Websites&quot; — check both.
      </Text>
      <Text fz={12}>3. Set it to While Using the App and turn on Precise Location.</Text>
      <Text fz={12}>4. Come back here and tap Locate again.</Text>
      <Text fz={12} fw="bold" mt={4}>
        Deleting the app and re-adding it to the Home Screen does NOT restore the
        prompt, and neither does Reload app below — that only reloads the page. The
        Settings toggle is the only thing that clears an OS-level denial.
      </Text>
      {/* say WHY these steps are showing when the browser would not tell us:
          support needs to know this was inferred, not reported */}
      {deniedIsInferred && (
        <Text fz={11} c="#8B8B8B" mt={4}>
          Shown because the last attempt this session
          {inferredDenialAt !== null
            ? `, ${describeDenialEvidenceAge(inferredDenialAt)} (${new Date(
                inferredDenialAt
              ).toLocaleString()}),`
            : ""}{" "}
          was denied — this browser reported the permission state as &quot;
          {env?.permissionState}&quot;. If you have changed the Settings toggle
          since then, tap Locate again to confirm.
        </Text>
      )}
    </Stack>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Location diagnostics"
      size="lg"
      zIndex={6000}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="sm">
        {row("Permission", <Badge color="gray">{env?.permissionState ?? "…"}</Badge>)}
        {denied && deniedRecovery}
        {row("Installed (standalone)", String(env?.standalone ?? "…"))}
        {row("navigator.standalone", String(env?.navigatorStandalone ?? "n/a"))}
        {row("isSecureContext", String(env?.isSecureContext ?? "…"))}
        {row("'geolocation' in navigator", String(env?.hasGeolocation ?? "…"))}
        {row("origin", env?.origin ?? "…")}
        {row(
          "Last position",
          diagnostics?.lastPosition
            ? `${diagnostics.lastPosition.lat.toFixed(5)}, ${diagnostics.lastPosition.lon.toFixed(5)} (±${Math.round(diagnostics.lastPosition.accuracy)}m)`
            : "none"
        )}
        {row("Last position age", formatAge(diagnostics?.lastPositionAgeMs ?? null))}
        {row("Request in flight", String(diagnostics?.inFlight ?? "…"))}
        {row("Dot subscribers", String(diagnostics?.subscriberCount ?? "…"))}

        <Text fz={13} fw="bold" mt="sm">Recent attempts (newest last)</Text>
        {diagnostics && diagnostics.entries.length > 0 ? (
          <ScrollArea.Autosize mah={260}>
            <Table fz={11} withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  {/* "When", not "Time": the column carries a date now. This
                      table is where entries restored from a PREVIOUS page
                      session appear (they stay visible but never vote — see
                      `lastRealAttempt`), and a time-only column could not tell
                      Monday's denial from this morning's. */}
                  <Table.Th>When</Table.Th>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>ms</Table.Th>
                  <Table.Th>Result</Table.Th>
                  <Table.Th>Detail</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {diagnostics.entries.map((e, i) => (
                  <Table.Tr key={`${e.t}-${i}`}>
                    <Table.Td>{formatDiagnosticTimestamp(e.t)}</Table.Td>
                    <Table.Td>{e.source}</Table.Td>
                    <Table.Td>{e.durationMs}</Table.Td>
                    <Table.Td c={e.ok ? "green" : "#E64B37"}>
                      {e.ok ? "ok" : `err${e.code !== null ? ` (${e.code})` : ""}`}
                    </Table.Td>
                    <Table.Td>
                      {e.ok
                        ? (e.accuracy !== null ? `±${Math.round(e.accuracy)}m` : "")
                        : e.message}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        ) : (
          <Text fz={12} c="#8B8B8B">No attempts recorded yet.</Text>
        )}

        <Text fz={13} fw="bold" mt="sm">User agent</Text>
        <Code block fz={10}>{env?.userAgent ?? "…"}</Code>

        <Group justify="space-between" mt="md">
          <Button variant="light" onClick={onCopy}>
            {copyResult || "Copy diagnostics"}
          </Button>
          <Button color="#E64B37" onClick={() => window.location.reload()}>
            Reload app
          </Button>
        </Group>
        {/* the tester tried this button as his fix for a denied permission — it is
            kept as the escape hatch for a wedged page, but must not read as one */}
        <Text fz={11} c="#8B8B8B">
          Reload app reloads the page only. It cannot clear an OS-level location
          permission denial.
        </Text>
      </Stack>
    </Modal>
  );
};

export default LocationDebugPanel;
